export const PLATFORM_SUPPORT_ROLES = ['admin', 'superadmin', 'support_user'];
export const GLOBAL_SUPPORT_ROLES = ['admin', 'superadmin', 'overseer'];
export const TENANT_SUPPORT_ROLES = ['tenant_admin', 'support_user'];
export const PLATFORM_READ_ROLES = GLOBAL_SUPPORT_ROLES;
export const SUPPORT_QUEUE_ROLES = [...GLOBAL_SUPPORT_ROLES, ...TENANT_SUPPORT_ROLES];

export function canViewSupportQueue(role, { dealerActive = false } = {}) {
  return Boolean(dealerActive) || SUPPORT_QUEUE_ROLES.includes(role);
}

export function canViewGlobalSupportQueue(role) {
  return GLOBAL_SUPPORT_ROLES.includes(role);
}

export function isTenantSupportQueue(role) {
  return TENANT_SUPPORT_ROLES.includes(role);
}

export const SUPPORT_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'In Progress' },
  { value: 'closed', label: 'Resolved' },
  { value: 'wishlist', label: 'Wishlist' },
  { value: 'big_issue', label: 'Big Issue' },
];

export function supportStatusOptions(includeAll = false) {
  if (!includeAll) return SUPPORT_STATUS_OPTIONS;
  return [{ value: '', label: 'All Statuses' }, ...SUPPORT_STATUS_OPTIONS];
}

export const SUPPORT_CATEGORY_LABELS = {
  bug: 'Bug',
  feedback: 'Feedback',
  feature: 'Feature request',
  question: 'Question',
  other: 'Other',
  account: 'Account',
  devices: 'Devices',
  billing: 'Billing',
};

export function supportCategoryLabel(type) {
  return SUPPORT_CATEGORY_LABELS[type] || type || 'Question';
}

/** Categories offered when filing a ticket. Values match the backend allow-list. */
export const SUPPORT_FILE_CATEGORIES = [
  { value: 'bug', label: 'Bug / Problem' },
  { value: 'feature', label: 'Enhancement Request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

/** Server-side length limits for a new ticket. */
export const SUPPORT_FIELD_LIMITS = {
  title: 200,
  description: 8000,
};

export function supportStatusLabel(status) {
  return SUPPORT_STATUS_OPTIONS.find((option) => option.value === status)?.label || status || 'Open';
}

export function canFileSupportReport(role, { dealerActive = false } = {}) {
  if (dealerActive || !role || canViewGlobalSupportQueue(role)) return false;
  return true;
}

export function supportViews({ role, dealerActive = false } = {}) {
  if (dealerActive) {
    return [{
      id: 'dealer',
      label: 'Dealer Tickets',
      description: 'Support requests from your assigned customers.',
    }];
  }
  if (canViewGlobalSupportQueue(role)) {
    return [{
      id: 'platform',
      label: 'Signal Bridge Support',
      description: 'Manage platform support and escalated requests.',
    }];
  }
  const views = [{
    id: 'mine',
    label: 'My Tickets',
    description: 'Track your support requests and replies.',
  }];
  if (isTenantSupportQueue(role)) {
    views.push({
      id: 'tenant',
      label: 'Tenant Tickets',
      description: 'Manage support requests from users in your tenant.',
    });
  }
  return views;
}

export function defaultSupportView(role, { dealerActive = false } = {}) {
  if (dealerActive) return 'dealer';
  if (canViewGlobalSupportQueue(role)) return 'platform';
  if (isTenantSupportQueue(role)) return 'tenant';
  return 'mine';
}

export function supportEmptyMessage(viewId) {
  if (viewId === 'mine') return 'You have no support requests yet.';
  if (viewId === 'tenant') return 'There are no support requests for your tenant.';
  if (viewId === 'dealer') return 'There are no support requests from your assigned customers.';
  return 'No reports found matching your filters.';
}

export function canEscalateSupportReport(role, { dealerActive = false } = {}) {
  return Boolean(dealerActive) || role === 'tenant_admin' || PLATFORM_SUPPORT_ROLES.includes(role);
}

export function canEditReleaseNotes(role) {
  return role === 'admin' || role === 'superadmin';
}

export function supportQueueTitle(role) {
  if (canViewGlobalSupportQueue(role)) return 'Signal Bridge Support';
  return 'Support';
}
